import React, { useState } from 'react';
import { toast } from 'react-toastify';
import TextField from '@mui/material/TextField';
import { formatDate } from '../../utils/dateUtils';
import CaseTicketService from './CaseTicketService';
import { toDateOnlyString } from './constants';

const baht = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Weight help is a share of a fee nobody knows yet (the parcel has not been
// weighed), so it is a PERCENTAGE, never baht — and it only matters if the parcel
// turns up. Refunding now is a different decision altogether, so it is asked first.
const NONE = 0;
const FREE = 100;
// Don't wait for the deadline: the parcel is treated as lost now and the
// admin's ✓ แจ้งลูกค้าแล้ว files the refund.
const REFUND = 'refund';

// Japan had nothing. The admin has already said which round they expect each
// parcel on, so the refund deadline is set. The supervisor decides, per parcel:
//   ① keep hunting (until the deadline) — or refund now
//   ② if hunting: how much of the weight fee to waive when it arrives
const SupervisorAssistPanel = ({ caseTicket, items, onDone }) => {
  // Nothing is chosen until it is chosen: an untouched parcel must not slide
  // through as "no discount" — that is a promise nobody made.
  const [paths, setPaths] = useState({});     // itemId -> 'hunt' | 'refund'
  const [modes, setModes] = useState({});     // itemId -> 'none' | 'free' | 'percent' (when hunting)
  const [percents, setPercents] = useState({}); // itemId -> string, when mode is 'percent'
  const [extraNote, setExtraNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Only what Japan has given up on AND nobody has ruled on yet. The warehouse
  // answers in batches, so by the time the second batch arrives some parcels
  // already carry a promise — those are settled and must not be asked again.
  const missing = items.filter((it) => it.foundStatus === 'pending' && it.warehouseAnsweredAt && !it.assistDecidedAt);
  const alreadyDecided = items.filter((it) => it.foundStatus === 'pending' && it.warehouseAnsweredAt && it.assistDecidedAt);
  const stillLooking = items.filter((it) => it.foundStatus === 'pending' && !it.warehouseAnsweredAt).length;
  const hasMissing = missing.length > 0;

  const setMode = (id, mode) => {
    setModes((prev) => ({ ...prev, [id]: mode }));
    if (mode === 'percent' && percents[id] === undefined) setPercents((prev) => ({ ...prev, [id]: '50' }));
  };

  // What each parcel resolves to, or null while it is still undecided.
  const decisionFor = (it) => {
    if (paths[it.id] === 'refund') return REFUND;
    if (paths[it.id] !== 'hunt') return null;
    const mode = modes[it.id];
    if (mode === 'none') return NONE;
    if (mode === 'free') return FREE;
    if (mode !== 'percent') return null;
    const n = Number(percents[it.id]);
    return percents[it.id] !== '' && Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
  };
  const describe = (v) => (v === REFUND ? 'คืนเงินเลย'
    : v >= FREE ? 'ตามหาต่อ · ฟรีค่าน้ำหนัก' : v > NONE ? `ตามหาต่อ · ลดค่าน้ำหนัก ${v}%` : 'ตามหาต่อ · ไม่ลดค่าน้ำหนัก');

  const undecided = missing.filter((it) => decisionFor(it) === null);

  const submit = async () => {
    if (undecided.length) { toast.error(`ยังไม่ได้ตัดสินใจ ${undecided.length} รายการ`); return; }

    const summary = missing.map((it) => `• ${describe(decisionFor(it))}`).join('\n');
    const refundNow = missing.some((it) => decisionFor(it) === REFUND)
      ? '\nรายการที่คืนเงินเลย: แอดมินส่งข้อความแล้วกด ✓ แจ้งลูกค้าแล้ว ระบบจะสร้างรายการคืนเงินทันที'
      : '';
    if (!window.confirm(`ยืนยันการตัดสินใจ?\n${summary}\n\nระบบจะร่างข้อความให้แอดมินไปแจ้งลูกค้า${refundNow}`)) return;

    const payload = Object.fromEntries(missing.map((it) => [it.id, decisionFor(it)]));
    setSaving(true);
    try {
      await CaseTicketService.setAssist(caseTicket.id, payload, extraNote.trim() || undefined);
      toast.success('บันทึกแล้ว — ร่างข้อความให้แอดมินแล้ว');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const today = toDateOnlyString(new Date());
  // Waiting on the supervisor only for a ตกหล่น find → that panel is the decision.
  if (!hasMissing) return null;

  const pill = (on, onCls, offCls) => `text-xs px-3 py-1.5 rounded-full border font-medium ${on ? onCls : `bg-white ${offCls}`}`;

  return (
    <div className="border-2 border-blue-500 bg-blue-50 rounded-lg p-4 mb-4">
      <h3 className="font-bold text-blue-900 mb-1">💰 หัวหน้าตัดสินใจ — {missing.length} รายการที่ Kluay หาไม่เจอ</h3>
      <p className="text-sm text-blue-800 mb-3">
        แต่ละรายการ: <b>① ตามหาต่อ หรือ คืนเงินเลย</b> → ถ้าตามหาต่อ <b>② ช่วยค่าน้ำหนักเท่าไหร่ถ้าของมาถึง</b>
        {stillLooking > 0 && <> · <b>อีก {stillLooking} รายการ Kluay ยังหาอยู่</b> — ตัดสินใจรายการที่รู้ผลแล้วไปก่อนได้</>}
      </p>

      {alreadyDecided.length > 0 && (
        <div className="bg-white/70 border border-blue-100 rounded p-2 mb-3 text-xs text-gray-600">
          <div className="font-medium text-gray-700 mb-1">ตัดสินใจไปแล้ว {alreadyDecided.length} รายการ (ไม่ต้องเลือกซ้ำ)</div>
          {alreadyDecided.map((it) => (
            <div key={it.id} className="truncate">
              • {it.trackingJp && it.trackingJp !== 'ไม่มีเลข' ? it.trackingJp : it.url} —{' '}
              <b className="text-blue-700">{describe(Number(it.assistPercent) || 0)}</b>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 mb-3">
        {missing.map((it) => {
          const path = paths[it.id];
          const mode = modes[it.id];
          const notYet = decisionFor(it) === null;
          const refundBaht = Number(it.refundPreview) || 0;
          // The round it was expected on already passed its refund date (case #9) —
          // a "hunt until <date>" promise would be for a date already gone.
          const overdue = !!it.refundDueAt && it.refundDueAt < today;
          return (
            <div key={it.id} className={`bg-white border rounded p-3 ${notYet ? 'border-amber-300' : 'border-blue-100'}`}>
              <div className="min-w-0 mb-2">
                <a href={it.url} target="_blank" rel="noreferrer" className="block truncate text-blue-700 underline text-sm">{it.url}</a>
                <div className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                  <span>{it.priceYen != null ? `${Number(it.priceYen).toLocaleString()} เยน` : '—'}</span>
                  <span>{it.trackingJp && it.trackingJp !== 'ไม่มีเลข' ? it.trackingJp : 'ไม่มีเลขพัสดุ'}</span>
                  {it.expectedShipDate && (
                    <span className="text-blue-700">
                      {it.misroutedAt ? 'ส่งออกมากับรอบ' : 'คาดว่ามารอบ'} {it.expectedShipDate.isAir ? '✈️ แอร์' : '🚢 เรือ'} {formatDate(it.expectedShipDate.shipDate)}
                    </span>
                  )}
                </div>
                {it.misroutedAt && (
                  <div className="text-xs text-amber-700 font-medium mt-1">
                    📦 ส่งผิดขนส่ง — โกดังกลางกำลังตามกลับ (ลูกค้าจะได้รับข้อความขออภัย ช้ากว่าปกติประมาณ 3-7 วัน)
                  </div>
                )}
              </div>

              {/* ① the path — hunting on vs refunding now; the baht belongs HERE only */}
              <div className="text-xs font-semibold text-gray-600 mb-1">① จะทำอย่างไรกับรายการนี้</div>
              {overdue && (
                <p className="text-sm text-red-700 font-medium mb-1">
                  ⚠️ เลยกำหนดคืนเงินแล้ว ({formatDate(it.refundDueAt)}) — ให้เลือกคืนเงินเลย
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <button type="button" disabled={overdue} onClick={() => setPaths((p) => ({ ...p, [it.id]: 'hunt' }))}
                  className={`${pill(path === 'hunt', 'bg-blue-600 text-white border-blue-600', 'text-blue-700 border-blue-300')} disabled:opacity-40 disabled:cursor-not-allowed`}>
                  {it.misroutedAt ? '📦 รอนำกลับ' : '🔎 ตามหาต่อ'}{overdue ? ' (เลยกำหนดแล้ว)' : it.refundDueAt ? ` (ถ้าไม่ได้รับภายใน ${formatDate(it.refundDueAt)} คืนเงิน)` : ''}
                </button>
                <button type="button" onClick={() => setPaths((p) => ({ ...p, [it.id]: 'refund' }))}
                  className={pill(path === 'refund', 'bg-purple-600 text-white border-purple-600', 'text-purple-700 border-purple-300')}>
                  💸 คืนเงินเลย {refundBaht > 0 ? `฿${baht(refundBaht)}` : '(ยังไม่ได้ชำระ — ยกเลิกออเดอร์)'}
                </button>
              </div>

              {/* ② only when hunting on: a share of the weight fee, applied if it arrives */}
              {path === 'hunt' && (
                <div className="border-t border-gray-100 pt-2">
                  <div className="text-xs font-semibold text-gray-600 mb-1">② ช่วยค่าน้ำหนักเมื่อของมาถึง (ยังไม่รู้น้ำหนัก จึงเป็น %)</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => setMode(it.id, 'none')}
                      className={pill(mode === 'none', 'bg-gray-600 text-white border-gray-600', 'text-gray-700 border-gray-300')}>ไม่ลด</button>
                    <button type="button" onClick={() => setMode(it.id, 'free')}
                      className={pill(mode === 'free', 'bg-emerald-600 text-white border-emerald-600', 'text-emerald-700 border-emerald-300')}>ฟรีค่าน้ำหนัก</button>
                    <button type="button" onClick={() => setMode(it.id, 'percent')}
                      className={pill(mode === 'percent', 'bg-blue-600 text-white border-blue-600', 'text-blue-700 border-blue-300')}>ลดเป็น %</button>
                    {mode === 'percent' && (
                      <TextField size="small" sx={{ width: 110, bgcolor: 'white' }} label="%" inputMode="decimal"
                        value={percents[it.id] ?? ''}
                        onChange={(e) => setPercents((prev) => ({ ...prev, [it.id]: e.target.value.replace(/[^\d.]/g, '') }))} />
                    )}
                  </div>
                </div>
              )}

              {notYet && <p className="text-xs text-amber-700 mt-1">ยังไม่ได้เลือก{path === 'hunt' ? ' ② ค่าน้ำหนัก' : ''}</p>}
            </div>
          );
        })}
      </div>

      <TextField fullWidth size="small" multiline minRows={1} sx={{ bgcolor: 'white', mb: 2 }}
        label="ข้อความเพิ่มเติมถึงลูกค้า (ไม่บังคับ)" value={extraNote} onChange={(e) => setExtraNote(e.target.value)} />

      <button onClick={submit} disabled={saving || undecided.length > 0}
        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg font-medium">
        บันทึก & ร่างข้อความให้แอดมิน
      </button>
      {undecided.length > 0 && (
        <p className="text-xs text-amber-700 mt-1">ยังไม่ได้ตัดสินใจ {undecided.length} รายการ — เลือกให้ครบก่อนถึงจะบันทึกได้</p>
      )}
    </div>
  );
};

export default SupervisorAssistPanel;
